import { useCallback, useMemo } from 'react';
import { selector, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { groupSettingsLoadedState, groupSettingsState } from '../../store/groupSettingsAtoms';
import { GroupSettings } from '../../types/common';
import { InvestmentThesis, Workspace } from '../../types/files';

const groupSettingsSelector = selector({
  key: 'groupSettingsSelector',
  get: ({ get }) => {
    const settingsLoaded = get(groupSettingsLoadedState);
    const settings = get(groupSettingsState);

    return {
      settingsLoaded,
      group: settings?.group ?? '',
      statuses: settings?.statuses?.map(status => status ? status.trim().toLowerCase() : '<rename this statuses>') ?? [],
      investmentStages: [...(settings?.investmentStages ?? [])].sort((a, b) => (a > b ? 1 : 0)),
      investmentThesis: settings?.investmentThesis ?? null,
      tags: [...(settings?.tags ?? [])].sort((a, b) => (a > b ? 1 : 0)),
      shouldSyncAllDashboardsExternally: settings?.shouldSyncAllDashboardsExternally ?? false,
      shouldIngestDashboardsFromEmail: settings?.shouldIngestDashboardsFromEmail ?? false,
      shouldSendDigestEmail: settings?.shouldSendDigestEmail ?? false,
      groupWorkspaces: settings?.workspaces ?? [],
      excludedCompanyTerms: settings?.excludedCompanyTerms ?? [],
    };
  },
});

const useGroupSettingsContext = () => {
  const [settingsLoaded, setGroupSettingsLoaded] = useRecoilState(groupSettingsLoadedState);
  const setGroupSettings = useSetRecoilState(groupSettingsState);
  const {
    group,
    statuses,
    investmentStages,
    investmentThesis,
    tags,
    shouldSyncAllDashboardsExternally,
    shouldIngestDashboardsFromEmail,
    shouldSendDigestEmail,
    groupWorkspaces,
    excludedCompanyTerms,
  } = useRecoilValue(groupSettingsSelector);

  const setGroup = useCallback((group: string | ((prev: string) => string)) =>{
    setGroupSettings(prev => (typeof group === "function"
      ? ({...prev as GroupSettings, group: group(prev?.group || '')})
      : ({...prev as GroupSettings, group})))
  }, [setGroupSettings]);

  const setStatuses = useCallback((statuses: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof statuses === "function"
      ? (prev => ({...prev as GroupSettings, statuses: statuses(prev?.statuses ?? [])}))
      : (prev => ({...prev as GroupSettings, statuses})))
  , [setGroupSettings]);

  const setTags = useCallback((tags: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof tags === "function"
      ? (prev => ({...prev as GroupSettings, tags: tags(prev?.tags ?? [])}))
      : (prev => ({...prev as GroupSettings, tags})))
  , [setGroupSettings]);

  const setStages = useCallback((stages: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof stages === "function"
      ? (prev => ({...prev as GroupSettings, investmentStages: stages(prev?.investmentStages ?? [])}))
      : (prev => ({...prev as GroupSettings, investmentStages: stages})))
  , [setGroupSettings]);

  const setExternalSync = useCallback((externalSync: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof externalSync === "function"
      ? (prev => ({...prev as GroupSettings, shouldSyncAllDashboardsExternally: externalSync(prev?.shouldSyncAllDashboardsExternally || false)}))
      : (prev => ({...prev as GroupSettings, shouldSyncAllDashboardsExternally: externalSync})))
  , [setGroupSettings]);

  const setAutoIngestFromEmail = useCallback((autoIngestFromEmail: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof autoIngestFromEmail === "function"
      ? (prev => ({...prev as GroupSettings, shouldIngestDashboardsFromEmail: autoIngestFromEmail(prev?.shouldIngestDashboardsFromEmail || false)}))
      : (prev => ({...prev as GroupSettings, shouldIngestDashboardsFromEmail: autoIngestFromEmail})))
  , [setGroupSettings]);

  const setAutoSendDigestEmail = useCallback((autoSendDigestEmail: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof autoSendDigestEmail === "function"
      ? (prev => ({...prev as GroupSettings, shouldSendDigestEmail: autoSendDigestEmail(prev?.shouldSendDigestEmail || false)}))
      : (prev => ({...prev as GroupSettings, shouldSendDigestEmail: autoSendDigestEmail})))
  , [setGroupSettings]);

  const setInvestmentThesis = useCallback((investmentThesis: InvestmentThesis | null | ((prev?: InvestmentThesis | null) => InvestmentThesis | null)) =>
    setGroupSettings(typeof investmentThesis === "function"
      ? (prev => ({...prev as GroupSettings, investmentThesis: investmentThesis(prev?.investmentThesis) ?? undefined}))
      : (prev => ({...prev as GroupSettings, investmentThesis: investmentThesis ?? undefined})))
  , [setGroupSettings]);

  const setGroupWorkspaces = useCallback((workspaces: Workspace[] | ((prev: Workspace[]) => Workspace[])) =>
    setGroupSettings(typeof workspaces === "function"
      ? (prev => ({...prev as GroupSettings, workspaces: workspaces(prev?.workspaces ?? [])}))
      : (prev => ({...prev as GroupSettings, workspaces})))
  , [setGroupSettings]);

  const setExcludedCompanyTerms = useCallback((excludedCompanyTerms: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof excludedCompanyTerms === "function"
      ? (prev => ({...prev as GroupSettings, excludedCompanyTerms: excludedCompanyTerms(prev?.excludedCompanyTerms ?? [])}))
      : (prev => ({...prev as GroupSettings, excludedCompanyTerms})))
  , [setGroupSettings]);

  const context = useMemo(() => ({
    settingsLoaded,
    group,
    statuses,
    tags,
    stages: investmentStages,
    externalSync: shouldSyncAllDashboardsExternally,
    autoIngestFromEmail: shouldIngestDashboardsFromEmail,
    autoSendDigestEmail: shouldSendDigestEmail,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    setGroupSettingsLoaded,
    setGroupSettings,
    setGroup,
    setStatuses,
    setTags,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setInvestmentThesis,
    setGroupWorkspaces,
    setExcludedCompanyTerms,
  }), [
    settingsLoaded,
    group,
    statuses,
    tags,
    investmentStages,
    shouldSyncAllDashboardsExternally,
    shouldIngestDashboardsFromEmail,
    shouldSendDigestEmail,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    setGroupSettingsLoaded,
    setGroupSettings,
    setGroup,
    setStatuses,
    setTags,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setInvestmentThesis,
    setGroupWorkspaces,
    setExcludedCompanyTerms,
  ]);

  return context;
};

export default useGroupSettingsContext;